import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  IconCoffee, IconBeer, IconBowl, IconBread, IconCake, IconCookie,
  IconLeaf, IconSquareDot, IconSparkles, IconAlertCircle, IconClock,
  IconPhoneCall, IconSearch, IconShoppingCart, IconX, IconPlus, IconMinus,
  IconCheck, IconChevronDown, IconHeart, IconStar, IconGift, IconCrown,
  IconExternalLink, IconBuildingStore,
} from '@tabler/icons-react';
import ProductImage from '../components/ProductImage';

/**
 * Customer-facing menu — Phase 6.2 (read-only browse + local cart).
 *
 *   GET /t/:code   — public, no auth.
 *
 * Light-themed, app-like browse experience with a local-only cart that
 * lets the customer build their order and call the waiter to ring it
 * up at the till. No payment / no API submission yet (Phase 6.3).
 *
 * Forces light mode regardless of the app's saved theme or the
 * browser's "auto-dark websites" feature.
 */

interface ApiTable  { id: string; name: string; code: string; capacity: number; area: string | null }
interface ApiBranch { id: string; name: string; code: string }
interface Business  { name: string; phone: string | null; address: string | null }
interface ApiBanner {
  id: string;
  imageUrl: string;
  title: string | null;
  subtitle: string | null;
  linkUrl: string | null;
  linkProductId: string | null;
  linkCategoryId: string | null;
}
interface ApiCategory {
  id: string; name: string; slug: string; tone: string; iconKey: string; sortOrder: number;
}
interface ApiProduct {
  id: string; categoryId: string; name: string; description: string | null;
  priceCents: number; originalPriceCents: number | null;
  diet: 'VEG' | 'NON_VEG' | 'EGG';
  badge: 'NONE' | 'TRENDING' | 'MUST_TRY' | 'HOT';
  imageUrl: string | null;
  requiresConfiguration: boolean;
  sizes: { name: string; priceDelta: number }[];
  defaultIngredients: string[];
  availableAddons: { name: string; priceCents: number }[];
  available: number;
}

interface CartLine {
  id: string;
  productId: string;
  name: string;
  size: string;
  priceDelta: number;
  basePrice: number;
  excludes: string[];
  addons: { name: string; priceCents: number }[];
  note: string;
  qty: number;
}

// Tone palette — uses the existing semantic tokens (defined in tailwind.config.ts)
// so the same colours show on POS, admin, and here. Solid bg, white text.
const TONE_TILE: Record<string, string> = {
  primary:   'bg-primary',
  secondary: 'bg-secondary',
  orange:    'bg-orange',
  info:      'bg-info',
  success:   'bg-success',
  warning:   'bg-warning',
  pink:      'bg-pink',
  rose:      'bg-rose',
  purple:    'bg-purple',
  indigo:    'bg-indigo',
  teal:      'bg-teal',
  lime:      'bg-lime',
  danger:    'bg-danger',
};
const toneOf = (t: string): string => (t in TONE_TILE ? t : 'primary');

const ICON_BY_KEY: Record<string, typeof IconSparkles> = {
  coffee: IconCoffee, beer: IconBeer, bowl: IconBowl, bread: IconBread,
  cake: IconCake, cookie: IconCookie, leaf: IconLeaf, sparkles: IconSparkles,
  circle: IconSquareDot,
};

const DIET_LABEL: Record<string, string> = { VEG: 'Veg', NON_VEG: 'Non veg', EGG: 'Egg' };
const DIET_DOT  : Record<string, string> = { VEG: 'bg-success', NON_VEG: 'bg-danger', EGG: 'bg-warning' };

function fmtR(cents: number) { return `R ${(cents / 100).toFixed(2)}`; }
function newId() { return (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`; }

function lineTotal(l: CartLine): number {
  return ((l.basePrice + l.priceDelta) + l.addons.reduce((n, a) => n + a.priceCents, 0)) * l.qty;
}

export default function PublicMenuPage() {
  const { code } = useParams<{ code: string }>();
  const [table, setTable]           = useState<ApiTable | null>(null);
  const [branch, setBranch]         = useState<ApiBranch | null>(null);
  const [business, setBusiness]     = useState<Business | null>(null);
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [products, setProducts]     = useState<ApiProduct[]>([]);
  const [banners, setBanners]       = useState<ApiBanner[]>([]);
  const [activeCat, setActiveCat]   = useState<string>('all');
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  // Cart state — persisted to sessionStorage so a tab refresh / iOS Safari
  // backgrounding doesn't lose the build-up. Keyed on the table code so
  // different tables don't share carts.
  const cartKey = `cart:${code ?? ''}`;
  const [cart, setCart] = useState<CartLine[]>(() => {
    try { return JSON.parse(sessionStorage.getItem(cartKey) ?? '[]'); } catch { return []; }
  });
  const [configuring, setConfiguring] = useState<ApiProduct | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);

  // Submission flow state — confirmation sheet + waiting screen.
  type PaymentIntent = 'CARD_AT_TABLE' | 'CASH_AT_COUNTER';
  type SubmittedRequest = {
    id: string;
    orderCode: string;
    kind: 'ORDER' | 'ASSISTANCE';
    paymentIntent: PaymentIntent | null;
    status: 'PENDING' | 'ACCEPTED' | 'CANCELLED' | 'EXPIRED' | 'PAID';
    tableName: string;
  };
  const reqKey = `request:${code ?? ''}`;
  const [pendingRequest, setPendingRequest] = useState<SubmittedRequest | null>(() => {
    try { return JSON.parse(sessionStorage.getItem(reqKey) ?? 'null'); } catch { return null; }
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [assistanceConfirmOpen, setAssistanceConfirmOpen] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  // Persist pending request id so a tab reload returns to the waiting state.
  useEffect(() => {
    try {
      if (pendingRequest) sessionStorage.setItem(reqKey, JSON.stringify(pendingRequest));
      else                sessionStorage.removeItem(reqKey);
    } catch { /* ignore */ }
  }, [pendingRequest, reqKey]);

  // Poll the status while we have a pending request that's not terminal.
  useEffect(() => {
    if (!pendingRequest) return;
    if (pendingRequest.status === 'PAID' || pendingRequest.status === 'CANCELLED' || pendingRequest.status === 'EXPIRED') return;
    let cancelled = false;
    async function tick() {
      try {
        const r = await fetch(`/api/public/table-requests/${pendingRequest!.id}`);
        if (!r.ok || cancelled) return;
        const data = await r.json();
        setPendingRequest((prev) => prev ? { ...prev, status: data.request.status } : prev);
      } catch { /* ignore network blips */ }
    }
    const id = window.setInterval(tick, 4000);
    void tick();
    return () => { cancelled = true; window.clearInterval(id); };
  }, [pendingRequest?.id, pendingRequest?.status]);

  useEffect(() => {
    try { sessionStorage.setItem(cartKey, JSON.stringify(cart)); } catch { /* ignore */ }
  }, [cart, cartKey]);

  // Force LIGHT colour scheme regardless of localStorage / browser auto-dark.
  useEffect(() => {
    const html = document.documentElement;
    const prevTheme = html.getAttribute('data-theme');
    const prevScheme = html.style.colorScheme;
    html.setAttribute('data-theme', 'light');
    html.style.colorScheme = 'light';
    return () => {
      if (prevTheme === null) html.removeAttribute('data-theme');
      else html.setAttribute('data-theme', prevTheme);
      html.style.colorScheme = prevScheme;
    };
  }, []);

  useEffect(() => {
    if (!code) return;
    (async () => {
      setLoading(true);
      try {
        const t = await fetch(`/api/tables/by-code/${code}`);
        if (!t.ok) throw new Error('That table doesn’t exist or is no longer active.');
        const td = await t.json();
        setTable(td.table);
        setBranch(td.branch);
        const m = await fetch(`/api/public/menu?branch=${encodeURIComponent(td.branch.code)}`);
        if (!m.ok) throw new Error(`Menu unavailable (HTTP ${m.status})`);
        const md = await m.json();
        setBusiness(md.business);
        setCategories(md.categories);
        setProducts(md.products);
        setBanners(md.banners ?? []);
        setError(null);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (activeCat !== 'all' && p.categoryId !== activeCat) return false;
      if (q && !p.name.toLowerCase().includes(q) && !(p.description ?? '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [products, activeCat, search]);

  const cartTotalCents  = cart.reduce((n, l) => n + lineTotal(l), 0);
  const cartItemsCount  = cart.reduce((n, l) => n + l.qty, 0);

  // Pulse the FAB whenever the item count increases.
  const [cartPulse, setCartPulse] = useState(false);
  const prevCount = useRef(cartItemsCount);
  useEffect(() => {
    if (cartItemsCount > prevCount.current) {
      setCartPulse(true);
      const t = window.setTimeout(() => setCartPulse(false), 520);
      prevCount.current = cartItemsCount;
      return () => window.clearTimeout(t);
    }
    prevCount.current = cartItemsCount;
  }, [cartItemsCount]);

  async function submitOrder(paymentIntent: PaymentIntent) {
    setSubmitErr(null);
    try {
      const r = await fetch('/api/public/table-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tableCode: code,
          kind: 'ORDER',
          paymentIntent,
          cart: cart.map((l) => ({
            productId: l.productId, name: l.name, size: l.size,
            priceDelta: l.priceDelta, basePrice: l.basePrice,
            excludes: l.excludes, addons: l.addons, note: l.note,
            qty: l.qty,
          })),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error ?? `HTTP ${r.status}`);
      setPendingRequest({
        id: data.request.id,
        orderCode: data.request.orderCode,
        kind: 'ORDER',
        paymentIntent,
        status: 'PENDING',
        tableName: data.request.tableName,
      });
      setConfirmOpen(false);
      setCartOpen(false);
    } catch (e) {
      setSubmitErr((e as Error).message);
    }
  }

  async function submitAssistance() {
    setSubmitErr(null);
    try {
      const r = await fetch('/api/public/table-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tableCode: code, kind: 'ASSISTANCE' }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error ?? `HTTP ${r.status}`);
      setPendingRequest({
        id: data.request.id,
        orderCode: data.request.orderCode,
        kind: 'ASSISTANCE',
        paymentIntent: null,
        status: 'PENDING',
        tableName: data.request.tableName,
      });
      setAssistanceConfirmOpen(false);
    } catch (e) {
      setSubmitErr((e as Error).message);
    }
  }

  async function cancelPendingRequest() {
    if (!pendingRequest) return;
    try {
      const r = await fetch(`/api/public/table-requests/${pendingRequest.id}/cancel`, { method: 'POST' });
      if (r.ok) {
        setPendingRequest((p) => p ? { ...p, status: 'CANCELLED' } : p);
        window.setTimeout(() => setPendingRequest(null), 2500);
      } else {
        const data = await r.json().catch(() => ({}));
        setSubmitErr(data?.error ?? `HTTP ${r.status}`);
      }
    } catch (e) {
      setSubmitErr((e as Error).message);
    }
  }

  // Clear cart + pending state on terminal status.
  useEffect(() => {
    if (pendingRequest?.status === 'PAID') {
      setCart([]);
      // Keep the "thanks!" screen for ~6s, then return to browse.
      const t = window.setTimeout(() => setPendingRequest(null), 6000);
      return () => window.clearTimeout(t);
    }
  }, [pendingRequest?.status]);

  function addToCart(line: CartLine) { setCart((c) => [...c, line]); }
  function removeLine(id: string) { setCart((c) => c.filter((l) => l.id !== id)); }
  function bumpLine(id: string, delta: number) {
    setCart((c) => c.map((l) => l.id === id ? { ...l, qty: Math.max(1, l.qty + delta) } : l));
  }

  if (loading) return <CenteredState icon={IconClock} title="Loading menu…" />;
  if (error)   return <CenteredState icon={IconAlertCircle} title="Hmm" subtitle={error} tone="danger" />;

  return (
    <div className="public-menu-page min-h-[100dvh] bg-slate-50 text-slate-900" style={{ colorScheme: 'light' }}>
      {/* Unified red hero — header + carousel sit on one continuous module.
          Two drifting blurred blobs give the gradient visible motion, an
          SVG-feTurbulence grain layer adds paper texture. */}
      <section className="hero-gradient relative text-white overflow-hidden shadow-md">
        <div className="hero-blob-1" />
        <div className="hero-blob-2" />
        <div className="hero-grain" />

        {/* Hero copy */}
        <header className="relative px-5 pt-7 pb-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-11 font-bold uppercase tracking-[0.25em] text-white/80">Welcome to</p>
              <h1 className="text-30 font-bold text-white leading-tight mt-1.5 drop-shadow-sm">
                {business?.name ?? branch?.name ?? 'HL Coffee Shop'}
              </h1>
              <p className="text-13 font-medium text-white/85 mt-2 leading-snug">
                You're at <strong className="text-white">{table?.name}</strong>
                {table?.area && <span className="text-white/65"> · {table.area}</span>}
              </p>
            </div>
            {/* Loyalty CTA */}
            <button
              type="button"
              onClick={() => setLoyaltyOpen(true)}
              className="flex-shrink-0 inline-flex flex-col items-center justify-center gap-0.5 w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/25 text-white text-10 font-bold uppercase tracking-wider hover:bg-white/25 active:scale-[0.96] transition-all"
              aria-label="Join loyalty"
            >
              <IconCrown size={20} stroke={2.25} />
              <span className="leading-none">Join</span>
            </button>
          </div>

          <div className="mt-4 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20 px-4 py-3">
            <p className="text-14 font-bold text-white leading-tight">Quick &amp; easy ordering</p>
            <p className="text-13 text-white/90 mt-1 leading-snug">
              Order directly from your phone. Your order will be brought to you in no time.
            </p>
            <p className="text-12 text-white/75 mt-1.5 leading-snug">
              Browse the menu below and place your order.
            </p>
          </div>
        </header>

        {/* Promotional banner carousel — shares the same red gradient module */}
        {banners.length > 0 && (
          <div className="relative pb-6 pt-1">
            <div className="overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-1 px-4 scroll-px-4">
              <div className="flex gap-3">
                {banners.map((b) => (
                  <BannerCard
                    key={b.id}
                    banner={b}
                    onProductLink={(productId) => {
                      const p = products.find((x) => x.id === productId);
                      if (p) setConfiguring(p);
                    }}
                    onCategoryLink={(categoryId) => {
                      setActiveCat(categoryId);
                      setTimeout(() => {
                        window.scrollTo({ top: window.innerHeight * 0.5, behavior: 'smooth' });
                      }, 50);
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Sticky controls */}
      <div className="sticky top-0 z-20 bg-slate-50/95 backdrop-blur-sm border-b border-slate-200">
        <div className="px-4 pt-3">
          <div className="relative">
            <IconSearch size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search the menu"
              className="w-full h-12 pl-[52px] pr-5 rounded-full bg-white border border-slate-200 focus:border-black outline-none text-14 font-medium text-slate-900 placeholder-slate-400 transition-colors"
            />
          </div>
        </div>
        <div className="px-4 py-3 overflow-x-auto">
          <div className="flex gap-2 w-max">
            <CategoryChip active={activeCat === 'all'} onClick={() => setActiveCat('all')} label="All" count={products.length} />
            {categories.map((c) => {
              const count = products.filter((p) => p.categoryId === c.id).length;
              return (
                <CategoryChip
                  key={c.id}
                  active={activeCat === c.id}
                  onClick={() => setActiveCat(c.id)}
                  iconKey={c.iconKey}
                  label={c.name}
                  count={count}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Products */}
      <main className="px-4 pt-4 pb-32">
        {visible.length === 0 ? (
          <div className="text-center py-14">
            <p className="text-14 font-semibold text-slate-900">Nothing matches</p>
            <p className="text-13 text-slate-600 mt-1">Try a different category or search term.</p>
          </div>
        ) : (
          <ul className="grid grid-cols-2 gap-3">
            {visible.map((p, i) => {
              const cat = categories.find((c) => c.id === p.categoryId);
              const tone = toneOf(cat?.tone ?? 'primary');
              const soldOut = p.available <= 0;
              const Icon = ICON_BY_KEY[cat?.iconKey ?? 'circle'] ?? IconSparkles;
              return (
                <li key={p.id} className="fade-up" style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}>
                  <button
                    type="button"
                    disabled={soldOut}
                    onClick={() => setConfiguring(p)}
                    className={`group w-full text-left rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm h-full flex flex-col transition-shadow hover:shadow-md ${soldOut ? 'opacity-60' : ''}`}
                  >
                    <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
                      <ProductImage
                        src={p.imageUrl ?? undefined}
                        name={p.name}
                        seed={p.id}
                        bgClassName={TONE_TILE[tone]}
                        compact="name"
                      />
                      {p.badge !== 'NONE' && (
                        <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 h-6 rounded-full bg-orange text-white text-10 font-bold uppercase tracking-wider shadow-sm">
                          {p.badge.replace('_', ' ').toLowerCase()}
                        </span>
                      )}
                      <div className="absolute top-2 right-2">
                        <Icon size={20} stroke={2.25} className="text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.45)]" />
                      </div>
                      {soldOut && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <span className="inline-flex items-center px-3 h-7 rounded-full bg-danger text-white text-12 font-bold uppercase tracking-widest shadow-md">
                            Sold out
                          </span>
                        </div>
                      )}
                      {!soldOut && (
                        <span className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-black text-white inline-flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                          <IconPlus size={18} stroke={2.5} />
                        </span>
                      )}
                    </div>
                    <div className="p-3 flex-1 flex flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-14 font-bold text-slate-900 leading-snug line-clamp-2">{p.name}</p>
                        <span className={`inline-block w-2 h-2 rounded-full ${DIET_DOT[p.diet]} flex-shrink-0 mt-1.5`} title={DIET_LABEL[p.diet]} />
                      </div>
                      {p.description && <p className="text-12 text-slate-500 mt-1 line-clamp-2">{p.description}</p>}
                      <div className="mt-auto pt-3 flex items-baseline justify-between gap-1">
                        <p className="text-16 font-bold text-slate-900 tabular-nums leading-none">{fmtR(p.priceCents)}</p>
                        {(p.sizes.length > 1 || p.availableAddons.length > 0) && (
                          <span className="text-10 font-bold uppercase tracking-wider text-slate-500">+ options</span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </main>

      {/* Floating Cart pill — solid black, no border. Count sits next to
          the cart icon as a notification-style badge. Pulses on add. */}
      {!cartOpen && (
        <button
          type="button"
          onClick={() => setCartOpen(true)}
          className={[
            'fixed bottom-4 left-1/2 -translate-x-1/2 z-30',
            'h-12 px-5 inline-flex items-center gap-3 rounded-full',
            'bg-black text-white text-14 font-semibold',
            'shadow-[0_10px_30px_-6px_rgba(0,0,0,0.45)]',
            'active:scale-[0.97] transition-transform duration-200',
            cartPulse ? 'cart-pulse' : '',
          ].join(' ')}
          style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
          aria-label="Open cart"
        >
          <span className="relative inline-flex items-center justify-center">
            <IconShoppingCart size={18} stroke={2.25} className="text-white" />
            {cartItemsCount > 0 && (
              <span className="absolute -top-2 -right-2.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white text-black text-10 font-bold tabular-nums ring-2 ring-black">
                {cartItemsCount}
              </span>
            )}
          </span>
          <span className="tabular-nums">
            {cartItemsCount === 0 ? 'Your cart' : fmtR(cartTotalCents)}
          </span>
        </button>
      )}

      {/* Habby & Lace online store CTA — ad-style, red gradient, centred */}
      <section className="cta-gradient-red text-white px-5 py-10 mt-4 relative overflow-hidden text-center">
        <div className="hero-grain" />
        <div className="relative max-w-md mx-auto">
          <div className="inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full bg-white/15 border border-white/25 text-10 font-bold uppercase tracking-widest text-white mb-3">
            <IconBuildingStore size={12} stroke={2.5} />
            Habby &amp; Lace
          </div>
          <h2 className="text-22 font-bold leading-tight text-white">Shop online,<br />delivered to your door</h2>
          <p className="text-13 text-white/90 mt-3 leading-snug">
            Homewares, décor, kitchen, bedding and more. Browse our full range
            at <strong className="text-white">habbyandlace.com</strong> and we'll bring it to you.
          </p>
          <a
            href="https://habbyandlace.com"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-red-700 text-14 font-bold shadow-lg active:scale-[0.97] transition-transform"
          >
            Shop online now
            <IconExternalLink size={15} stroke={2.5} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-slate-400 px-5 py-7">
        <div className="max-w-2xl mx-auto text-center space-y-3">
          <p className="text-12 text-slate-400 leading-relaxed">
            This is an authorised digital ordering portal for {business?.name ?? 'HL Coffee Shop'}.
            Habby &amp; Lace is not responsible for menu items not listed here, so please ask your waiter for any changes.
            All prices include VAT.
          </p>
          <div className="flex items-center justify-center gap-3 text-12">
            <a href="#" className="text-slate-300 hover:text-white underline underline-offset-2">Terms &amp; Conditions</a>
            <span className="text-slate-600">·</span>
            <a href="#" className="text-slate-300 hover:text-white underline underline-offset-2">Privacy</a>
            {business?.phone && (
              <>
                <span className="text-slate-600">·</span>
                <a href={`tel:${business.phone.replace(/\s+/g, '')}`} className="text-slate-300 hover:text-white underline underline-offset-2">{business.phone}</a>
              </>
            )}
          </div>
          <p className="text-11 text-slate-500 pt-1">© {new Date().getFullYear()} Habby &amp; Lace. All rights reserved.</p>
          <p className="text-10 text-slate-600 tracking-wide">
            Powered by <span className="font-bold">CODED.</span> <span className="italic">framework</span>
          </p>
        </div>
      </footer>

      {/* Configure modal */}
      {configuring && (
        <ConfigureSheet
          product={configuring}
          onClose={() => setConfiguring(null)}
          onAdd={(line) => { addToCart(line); setConfiguring(null); }}
        />
      )}

      {/* Loyalty modal */}
      {loyaltyOpen && <LoyaltyModal businessName={business?.name ?? branch?.name ?? 'HL Coffee Shop'} onClose={() => setLoyaltyOpen(false)} />}

      {/* Order placement: confirm payment method */}
      {confirmOpen && (
        <OrderConfirmSheet
          totalCents={cartTotalCents}
          itemsCount={cartItemsCount}
          tableName={table?.name ?? ''}
          submitErr={submitErr}
          onClose={() => { setConfirmOpen(false); setSubmitErr(null); }}
          onSubmit={submitOrder}
        />
      )}

      {/* Assistance confirm */}
      {assistanceConfirmOpen && (
        <AssistanceConfirmSheet
          tableName={table?.name ?? ''}
          submitErr={submitErr}
          onClose={() => { setAssistanceConfirmOpen(false); setSubmitErr(null); }}
          onSubmit={submitAssistance}
        />
      )}

      {/* Waiting state — appears once a request is pending or accepted */}
      {pendingRequest && (
        <WaitingScreen
          request={pendingRequest}
          submitErr={submitErr}
          onCancel={cancelPendingRequest}
          onDone={() => setPendingRequest(null)}
        />
      )}

      {/* Cart drawer */}
      {cartOpen && (
        <CartDrawer
          cart={cart}
          totalCents={cartTotalCents}
          tableName={table?.name ?? ''}
          summaryOpen={summaryOpen}
          onToggleSummary={() => setSummaryOpen((o) => !o)}
          onClose={() => setCartOpen(false)}
          onBump={bumpLine}
          onRemove={removeLine}
          onClear={() => setCart([])}
          onPlaceOrder={() => { setCartOpen(false); setConfirmOpen(true); }}
          onRequestAssistance={() => { setCartOpen(false); setAssistanceConfirmOpen(true); }}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Configure sheet — pick size, exclusions, add-ons, note, qty                */
/* -------------------------------------------------------------------------- */

function ConfigureSheet({
  product, onClose, onAdd,
}: {
  product: ApiProduct;
  onClose: () => void;
  onAdd: (line: CartLine) => void;
}) {
  const [size, setSize] = useState<string>(product.sizes?.[0]?.name ?? 'Regular');
  const sizeMeta = product.sizes.find((s) => s.name === size);
  const priceDelta = sizeMeta?.priceDelta ?? 0;
  const [excludes, setExcludes] = useState<Set<string>>(new Set());
  const [addons, setAddons]     = useState<Set<string>>(new Set());
  const [note, setNote]         = useState('');
  const [qty, setQty]           = useState(1);

  const addonsArr = product.availableAddons.filter((a) => addons.has(a.name));
  const addonSum  = addonsArr.reduce((n, a) => n + a.priceCents, 0);
  const unit      = product.priceCents + priceDelta + addonSum;
  const total     = unit * qty;

  function submit() {
    onAdd({
      id: newId(),
      productId: product.id,
      name: product.name,
      size,
      priceDelta,
      basePrice: product.priceCents,
      excludes: Array.from(excludes),
      addons: addonsArr,
      note: note.trim(),
      qty,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{ colorScheme: 'light' }}>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative w-full sm:max-w-md max-h-[90dvh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-slide-up">
        {/* Drag handle */}
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <span className="w-10 h-1 rounded-full bg-slate-300" />
        </div>
        {/* Header */}
        <div className="px-5 pt-3 pb-4 border-b border-slate-100 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-11 font-bold uppercase tracking-widest text-slate-500">Customise</p>
            <h3 className="text-20 font-bold text-slate-900 mt-0.5 leading-tight">{product.name}</h3>
            {product.description && <p className="text-13 text-slate-600 mt-1">{product.description}</p>}
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 text-slate-700 inline-flex items-center justify-center flex-shrink-0">
            <IconX size={18} stroke={2.25} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {product.sizes.length > 1 && (
            <section>
              <p className="text-12 font-bold uppercase tracking-wider text-slate-700 mb-2">Size</p>
              <div className="grid grid-cols-3 gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s.name}
                    onClick={() => setSize(s.name)}
                    className={[
                      'h-12 rounded-xl border-2 text-13 font-bold transition-colors',
                      size === s.name
                        ? 'bg-black border-black text-white'
                        : 'bg-white border-slate-200 text-slate-900 hover:border-slate-400',
                    ].join(' ')}
                  >
                    {s.name}
                    {s.priceDelta !== 0 && <span className="block text-11 font-medium opacity-80 mt-0.5">{s.priceDelta > 0 ? '+' : ''}{fmtR(s.priceDelta)}</span>}
                  </button>
                ))}
              </div>
            </section>
          )}

          {product.defaultIngredients.length > 0 && (
            <section>
              <p className="text-12 font-bold uppercase tracking-wider text-slate-700 mb-2">Ingredients <span className="font-medium normal-case tracking-normal text-slate-500">tap to remove</span></p>
              <div className="flex flex-wrap gap-2">
                {product.defaultIngredients.map((ing) => {
                  const removed = excludes.has(ing);
                  return (
                    <button
                      key={ing}
                      onClick={() => setExcludes((prev) => { const n = new Set(prev); n.has(ing) ? n.delete(ing) : n.add(ing); return n; })}
                      className={[
                        'inline-flex items-center gap-1.5 px-3 h-9 rounded-full border-2 text-13 font-semibold transition-colors',
                        removed
                          ? 'bg-danger/10 border-danger/30 text-danger line-through'
                          : 'bg-white border-slate-200 text-slate-900 hover:border-slate-400',
                      ].join(' ')}
                    >
                      {removed && <IconX size={12} stroke={2.5} />}
                      {ing}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {product.availableAddons.length > 0 && (
            <section>
              <p className="text-12 font-bold uppercase tracking-wider text-slate-700 mb-2">Add-ons</p>
              <ul className="space-y-2">
                {product.availableAddons.map((a) => {
                  const on = addons.has(a.name);
                  return (
                    <li key={a.name}>
                      <button
                        onClick={() => setAddons((prev) => { const n = new Set(prev); n.has(a.name) ? n.delete(a.name) : n.add(a.name); return n; })}
                        className={[
                          'w-full flex items-center justify-between gap-3 px-4 h-12 rounded-xl border-2 transition-colors',
                          on
                            ? 'bg-success/10 border-success/40 text-success'
                            : 'bg-white border-slate-200 text-slate-900 hover:border-slate-400',
                        ].join(' ')}
                      >
                        <span className="flex items-center gap-2.5 text-13 font-bold">
                          <span className={[
                            'w-5 h-5 rounded-md inline-flex items-center justify-center transition-colors',
                            on ? 'bg-success text-white' : 'bg-slate-100 text-slate-400',
                          ].join(' ')}>
                            {on ? <IconCheck size={14} stroke={3} /> : <IconPlus size={12} stroke={2.5} />}
                          </span>
                          {a.name}
                        </span>
                        <span className="text-13 font-bold tabular-nums">+{fmtR(a.priceCents)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section>
            <p className="text-12 font-bold uppercase tracking-wider text-slate-700 mb-2">Note <span className="font-medium normal-case tracking-normal text-slate-500">optional</span></p>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. extra hot, no sugar"
              className="w-full h-12 px-4 rounded-xl bg-white border-2 border-slate-200 focus:border-slate-900 outline-none text-14 font-medium text-slate-900 placeholder-slate-400 transition-colors"
            />
          </section>
        </div>

        {/* Footer — qty + add */}
        <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex items-center gap-3" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
          <div className="inline-flex items-center bg-white rounded-full border-2 border-slate-200 overflow-hidden">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-11 inline-flex items-center justify-center text-slate-700 disabled:opacity-30" disabled={qty <= 1}>
              <IconMinus size={16} stroke={2.5} />
            </button>
            <span className="px-3 text-15 font-bold tabular-nums text-slate-900 min-w-[28px] text-center">{qty}</span>
            <button onClick={() => setQty((q) => q + 1)} className="w-11 h-11 inline-flex items-center justify-center text-slate-700">
              <IconPlus size={16} stroke={2.5} />
            </button>
          </div>
          <button
            onClick={submit}
            className="flex-1 h-12 rounded-full bg-black text-white text-14 font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          >
            <IconPlus size={16} stroke={2.5} />
            Add · {fmtR(total)}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Cart drawer — review + "call the waiter"                                   */
/* -------------------------------------------------------------------------- */

function CartDrawer({
  cart, totalCents, tableName, summaryOpen, onToggleSummary, onClose,
  onBump, onRemove, onClear, onPlaceOrder, onRequestAssistance,
}: {
  cart: CartLine[];
  totalCents: number;
  tableName: string;
  summaryOpen: boolean;
  onToggleSummary: () => void;
  onClose: () => void;
  onBump: (id: string, delta: number) => void;
  onRemove: (id: string) => void;
  onClear: () => void;
  onPlaceOrder: () => void;
  onRequestAssistance: () => void;
}) {
  const vatCents = Math.round((totalCents * 15) / 115);
  const subCents = totalCents - vatCents;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{ colorScheme: 'light' }}>
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/50" />
      <div className="relative w-full sm:max-w-md max-h-[90dvh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-slide-up">
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <span className="w-10 h-1 rounded-full bg-slate-300" />
        </div>
        <div className="px-5 pt-3 pb-4 border-b border-slate-100 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-11 font-bold uppercase tracking-widest text-slate-500">Your cart</p>
            <h3 className="text-20 font-bold text-slate-900 mt-0.5 leading-tight">{tableName}</h3>
            <p className="text-13 text-slate-600 mt-1">{cart.length === 0 ? 'Nothing here yet' : `${cart.length} item${cart.length === 1 ? '' : 's'}`}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 text-slate-700 inline-flex items-center justify-center flex-shrink-0">
            <IconX size={18} stroke={2.25} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {cart.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 text-slate-500 inline-flex items-center justify-center mb-3">
                <IconShoppingCart size={26} stroke={2} />
              </div>
              <p className="text-14 font-bold text-slate-900">Your cart is empty</p>
              <p className="text-13 text-slate-600 mt-1">Browse the menu and tap to add.</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {cart.map((l) => (
                <li key={l.id} className="rounded-2xl border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-14 font-bold text-slate-900 leading-snug">{l.name}</p>
                      <p className="text-12 font-medium text-slate-500 mt-0.5">{l.size}</p>
                    </div>
                    <button onClick={() => onRemove(l.id)} className="w-7 h-7 rounded-full bg-slate-100 text-slate-500 hover:bg-danger hover:text-white inline-flex items-center justify-center transition-colors">
                      <IconX size={14} stroke={2.5} />
                    </button>
                  </div>
                  {(l.excludes.length > 0 || l.addons.length > 0 || l.note) && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {l.excludes.map((x) => (
                        <span key={x} className="inline-flex items-center gap-1 px-2 h-5 rounded-md bg-danger text-white text-10 font-bold uppercase tracking-wider">
                          <IconX size={10} stroke={3} /> No {x}
                        </span>
                      ))}
                      {l.addons.map((a) => (
                        <span key={a.name} className="inline-flex items-center gap-1 px-2 h-5 rounded-md bg-success text-white text-10 font-bold uppercase tracking-wider">
                          + {a.name}
                        </span>
                      ))}
                      {l.note && (
                        <span className="inline-flex items-center px-2 h-5 rounded-md bg-warning text-white text-10 font-bold italic">"{l.note}"</span>
                      )}
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="inline-flex items-center bg-slate-100 rounded-full overflow-hidden">
                      <button onClick={() => onBump(l.id, -1)} className="w-9 h-9 inline-flex items-center justify-center text-slate-700">
                        <IconMinus size={14} stroke={2.5} />
                      </button>
                      <span className="px-2 text-13 font-bold tabular-nums text-slate-900 min-w-[20px] text-center">{l.qty}</span>
                      <button onClick={() => onBump(l.id, 1)} className="w-9 h-9 inline-flex items-center justify-center text-slate-700">
                        <IconPlus size={14} stroke={2.5} />
                      </button>
                    </div>
                    <p className="text-15 font-bold tabular-nums text-slate-900">{fmtR(lineTotal(l))}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {cart.length > 0 && (
          <div className="px-5 pt-3 pb-4 border-t border-slate-100 bg-slate-50" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
            <button
              type="button"
              onClick={onToggleSummary}
              className="w-full flex items-center justify-between text-11 font-bold uppercase tracking-widest text-slate-500 mb-1"
            >
              <span className="inline-flex items-center gap-1.5">
                <IconChevronDown size={12} stroke={2.5} className={`transition-transform ${summaryOpen ? '' : '-rotate-90'}`} />
                Summary
              </span>
              <button type="button" onClick={(e) => { e.stopPropagation(); onClear(); }} className="text-11 font-bold uppercase tracking-widest text-danger hover:underline">Clear cart</button>
            </button>
            {summaryOpen && (
              <div className="space-y-1 mb-2">
                <div className="flex items-center justify-between text-13 text-slate-700"><span>Sub-total (excl. VAT)</span><span className="tabular-nums">{fmtR(subCents)}</span></div>
                <div className="flex items-center justify-between text-13 text-slate-700"><span>VAT 15% (included)</span><span className="tabular-nums">{fmtR(vatCents)}</span></div>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 mb-3 border-t border-slate-200">
              <p className="text-15 font-bold text-slate-900">Amount</p>
              <p className="text-22 font-bold tabular-nums text-slate-900">{fmtR(totalCents)}</p>
            </div>
            <button
              type="button"
              onClick={onPlaceOrder}
              className="w-full h-14 rounded-full bg-black text-white text-15 font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-lg"
            >
              Place order
              <IconCheck size={18} stroke={2.5} />
            </button>
            <button
              type="button"
              onClick={onRequestAssistance}
              className="w-full h-11 mt-2 rounded-full bg-white border border-slate-200 text-slate-900 text-13 font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <IconPhoneCall size={14} stroke={2.25} />
              Or request assistance
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Category chip                                                               */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* Loyalty modal — promo for joining the loyalty programme                    */
/* -------------------------------------------------------------------------- */

function LoyaltyModal({ businessName, onClose }: { businessName: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{ colorScheme: 'light' }}>
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/55" />
      <div className="relative w-full sm:max-w-md max-h-[90dvh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-up flex flex-col">
        {/* Hero band */}
        <div className="hero-gradient relative px-6 pt-7 pb-6 text-white overflow-hidden">
          <div className="hero-blob-1" />
          <div className="hero-grain" />
          <div className="relative flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 border border-white/25 inline-flex items-center justify-center flex-shrink-0">
              <IconCrown size={26} stroke={2.25} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-11 font-bold uppercase tracking-[0.2em] text-white/80">{businessName}</p>
              <h2 className="text-22 font-bold leading-tight mt-0.5 text-white">Join our loyalty programme</h2>
              <p className="text-13 text-white/85 leading-snug mt-1">
                Become a regular. Earn points on every order and unlock perks just for you.
              </p>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 text-white inline-flex items-center justify-center flex-shrink-0 hover:bg-white/25 transition-colors">
              <IconX size={18} stroke={2.25} />
            </button>
          </div>
        </div>

        {/* Perks */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <p className="text-11 font-bold uppercase tracking-widest text-slate-500 mb-3">What you get</p>
          <ul className="space-y-3">
            <Perk
              icon={IconStar}
              tone="bg-amber-100 text-amber-700"
              title="Earn points on every order"
              body="One point per R1 spent. Points stack across visits and unlock rewards as you go."
            />
            <Perk
              icon={IconGift}
              tone="bg-rose-100 text-rose-700"
              title="Free birthday drink"
              body="Tell us your birthday once. We'll send you a voucher for a free drink each year."
            />
            <Perk
              icon={IconHeart}
              tone="bg-red-100 text-red-700"
              title="Exclusive offers"
              body="Members-only specials, new-item early access, and surprise treats from the team."
            />
            <Perk
              icon={IconCrown}
              tone="bg-purple-100 text-purple-700"
              title="Tiers as you grow"
              body="The more you visit, the better the perks. Silver, gold and platinum tiers unlock as you spend."
            />
          </ul>

          <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-200 p-4">
            <p className="text-13 font-bold text-slate-900">Ready to join?</p>
            <p className="text-12 text-slate-600 mt-1 leading-snug">
              Tell your waiter you'd like to sign up. It takes 30 seconds at the till.
              You'll just need your name, phone number, and birthday (optional).
            </p>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="px-6 py-4 border-t border-slate-100 bg-white" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
          <button
            onClick={onClose}
            className="w-full h-12 rounded-full bg-black text-white text-14 font-bold active:scale-[0.98] transition-transform"
          >
            Got it, I'll ask my waiter
          </button>
        </div>
      </div>
    </div>
  );
}

function Perk({ icon: Icon, tone, title, body }: { icon: typeof IconStar; tone: string; title: string; body: string }) {
  return (
    <li className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-xl inline-flex items-center justify-center flex-shrink-0 ${tone}`}>
        <Icon size={18} stroke={2.25} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-14 font-bold text-slate-900 leading-tight">{title}</p>
        <p className="text-12 text-slate-600 mt-0.5 leading-snug">{body}</p>
      </div>
    </li>
  );
}

function BannerCard({
  banner, onProductLink, onCategoryLink,
}: {
  banner: ApiBanner;
  onProductLink: (productId: string) => void;
  onCategoryLink: (categoryId: string) => void;
}) {
  const inner = (
    <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-slate-800 shadow-md">
      <img src={banner.imageUrl} alt={banner.title ?? 'Banner'} className="w-full h-full object-cover" loading="lazy" />
      {(banner.title || banner.subtitle) && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
          {banner.title && <p className="text-15 font-bold text-white leading-tight">{banner.title}</p>}
          {banner.subtitle && <p className="text-12 font-medium text-white/85 leading-snug mt-0.5">{banner.subtitle}</p>}
        </div>
      )}
    </div>
  );

  // Tap routing: product > category > external url > no-op.
  let onTap: (() => void) | undefined;
  let href: string | undefined;
  if (banner.linkProductId)       onTap = () => onProductLink(banner.linkProductId!);
  else if (banner.linkCategoryId) onTap = () => onCategoryLink(banner.linkCategoryId!);
  else if (banner.linkUrl)        href  = banner.linkUrl;

  return (
    <div className="flex-shrink-0 w-[55vw] max-w-[260px] snap-start">
      {onTap ? (
        <button type="button" onClick={onTap} className="block w-full active:scale-[0.98] transition-transform">{inner}</button>
      ) : href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="block">{inner}</a>
      ) : (
        inner
      )}
    </div>
  );
}

function CategoryChip({
  active, onClick, iconKey, label, count,
}: {
  active: boolean; onClick: () => void; iconKey?: string; label: string; count: number;
}) {
  const Icon = iconKey ? ICON_BY_KEY[iconKey] ?? IconSparkles : IconSparkles;
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex-shrink-0 inline-flex items-center gap-2 px-4 h-10 rounded-full text-13 font-bold transition-all',
        active
          ? 'bg-black text-white shadow-sm'
          : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-400',
      ].join(' ')}
    >
      {iconKey && <Icon size={14} stroke={2.25} className={active ? 'opacity-90' : 'text-slate-500'} />}
      <span>{label}</span>
      <span className={[
        'tabular-nums text-10 font-bold px-1.5 py-0.5 rounded-full',
        active ? 'bg-white/20' : 'bg-slate-100 text-slate-700',
      ].join(' ')}>{count}</span>
    </button>
  );
}

function CenteredState({ icon: Icon, title, subtitle, tone }: { icon: typeof IconSparkles; title: string; subtitle?: string; tone?: 'danger' }) {
  const colour = tone === 'danger' ? 'text-danger' : 'text-slate-700';
  return (
    <div className="min-h-[100dvh] bg-slate-50 text-slate-900 flex items-center justify-center px-6" style={{ colorScheme: 'light' }}>
      <div className="text-center max-w-sm">
        <div className={`w-14 h-14 rounded-2xl bg-white inline-flex items-center justify-center mb-3 shadow-sm ${colour}`}>
          <Icon size={26} stroke={2} />
        </div>
        <p className="text-18 font-bold text-slate-900">{title}</p>
        {subtitle && <p className="text-14 font-medium text-slate-600 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Order confirmation sheet — recap + payment method picker                   */
/* -------------------------------------------------------------------------- */

type PI = 'CARD_AT_TABLE' | 'CASH_AT_COUNTER';

function OrderConfirmSheet({
  totalCents, itemsCount, tableName, submitErr, onClose, onSubmit,
}: {
  totalCents: number;
  itemsCount: number;
  tableName: string;
  submitErr: string | null;
  onClose: () => void;
  onSubmit: (intent: PI) => void;
}) {
  const [intent, setIntent] = useState<PI | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function go() {
    if (!intent) return;
    setSubmitting(true);
    await onSubmit(intent);
    setSubmitting(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{ colorScheme: 'light' }}>
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/55" />
      <div className="relative w-full sm:max-w-md max-h-[90dvh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-slide-up">
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <span className="w-10 h-1 rounded-full bg-slate-300" />
        </div>
        <div className="px-6 pt-3 pb-4 border-b border-slate-100 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-11 font-bold uppercase tracking-widest text-slate-500">Place your order</p>
            <h3 className="text-22 font-bold text-slate-900 mt-0.5 leading-tight">Choose your payment option</h3>
            <p className="text-13 text-slate-600 mt-1 leading-snug">
              {itemsCount} {itemsCount === 1 ? 'item' : 'items'} <span className="text-slate-400">·</span> <span className="tabular-nums font-bold text-slate-900">{fmtR(totalCents)}</span>
            </p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 text-slate-700 inline-flex items-center justify-center flex-shrink-0">
            <IconX size={18} stroke={2.25} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
          <PayChoice
            active={intent === 'CARD_AT_TABLE'}
            onClick={() => setIntent('CARD_AT_TABLE')}
            title="Pay with card at your table"
            body="Stay where you are. A waiter will bring the card machine over."
            badge="No need to get up"
          />
          <PayChoice
            active={intent === 'CASH_AT_COUNTER'}
            onClick={() => setIntent('CASH_AT_COUNTER')}
            title="Pay with cash at the counter"
            body="When you're ready, head to the counter. We'll have your order ready to ring up."
            badge="Walk-up to pay"
          />

          {submitErr && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 flex items-start gap-2">
              <IconAlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-13 font-semibold text-red-700">{submitErr}</p>
            </div>
          )}

          <p className="text-11 text-slate-500 text-center pt-1 leading-snug">
            You're at <strong className="text-slate-700">{tableName}</strong>. The cashier will match you to your order code.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
          <button
            type="button"
            onClick={() => void go()}
            disabled={!intent || submitting}
            className="w-full h-13 rounded-full bg-black text-white text-15 font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? 'Sending...' : (
              <>
                <IconCheck size={18} stroke={2.5} />
                Confirm order
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function PayChoice({ active, onClick, title, body, badge }: { active: boolean; onClick: () => void; title: string; body: string; badge: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'w-full text-left rounded-2xl border-2 p-4 transition-all',
        active ? 'border-black bg-slate-50' : 'border-slate-200 bg-white hover:border-slate-300',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <span className={[
          'w-5 h-5 rounded-full border-2 inline-flex items-center justify-center flex-shrink-0 mt-0.5',
          active ? 'bg-black border-black' : 'border-slate-300',
        ].join(' ')}>
          {active && <IconCheck size={12} stroke={3} className="text-white" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <p className="text-15 font-bold text-slate-900 leading-tight">{title}</p>
            <span className="inline-flex items-center px-2 h-5 rounded-full bg-emerald-50 border border-emerald-200 text-10 font-bold uppercase tracking-wider text-emerald-700">{badge}</span>
          </div>
          <p className="text-13 text-slate-600 mt-1 leading-snug">{body}</p>
        </div>
      </div>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Assistance confirmation sheet                                              */
/* -------------------------------------------------------------------------- */

function AssistanceConfirmSheet({ tableName, submitErr, onClose, onSubmit }: { tableName: string; submitErr: string | null; onClose: () => void; onSubmit: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{ colorScheme: 'light' }}>
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/55" />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-up">
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <span className="w-10 h-1 rounded-full bg-slate-300" />
        </div>
        <div className="px-6 pt-3 pb-5">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 inline-flex items-center justify-center mb-3">
            <IconPhoneCall size={24} stroke={2.25} className="text-slate-900" />
          </div>
          <h3 className="text-22 font-bold text-slate-900 leading-tight">Call your waiter over</h3>
          <p className="text-13 text-slate-600 mt-1 leading-snug">
            Have a question, want to change something, or just need a hand? We'll let your waiter know.
            You're at <strong className="text-slate-900">{tableName}</strong>.
          </p>
          {submitErr && (
            <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 flex items-start gap-2">
              <IconAlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-13 font-semibold text-red-700">{submitErr}</p>
            </div>
          )}
          <div className="flex items-stretch gap-2 mt-5" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <button onClick={onClose} className="flex-1 h-12 rounded-full bg-white border-2 border-slate-200 text-slate-900 text-14 font-bold">Cancel</button>
            <button
              onClick={async () => { setSubmitting(true); await onSubmit(); setSubmitting(false); }}
              disabled={submitting}
              className="flex-[2] h-12 rounded-full bg-black text-white text-14 font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-40"
            >
              {submitting ? 'Sending...' : 'Yes, call a waiter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Waiting screen — shown while a request is open                             */
/* -------------------------------------------------------------------------- */

function WaitingScreen({
  request, submitErr, onCancel, onDone,
}: {
  request: { id: string; orderCode: string; kind: 'ORDER' | 'ASSISTANCE'; paymentIntent: PI | null; status: 'PENDING' | 'ACCEPTED' | 'CANCELLED' | 'EXPIRED' | 'PAID'; tableName: string };
  submitErr: string | null;
  onCancel: () => void;
  onDone: () => void;
}) {
  const isOrder = request.kind === 'ORDER';
  const isCard  = request.paymentIntent === 'CARD_AT_TABLE';

  let title = '';
  let body  = '';
  let tone: 'pending' | 'accepted' | 'paid' | 'cancelled' = 'pending';

  if (request.status === 'PAID') {
    title = 'Thank you!';
    body  = 'Payment received. Enjoy your time at the café.';
    tone  = 'paid';
  } else if (request.status === 'CANCELLED' || request.status === 'EXPIRED') {
    title = request.status === 'EXPIRED' ? 'Order expired' : 'Order cancelled';
    body  = 'Tap below to start a new one.';
    tone  = 'cancelled';
  } else if (request.status === 'ACCEPTED') {
    if (!isOrder) {
      title = 'A waiter is on the way';
      body  = "They'll be with you shortly.";
    } else if (isCard) {
      title = 'A waiter is on the way';
      body  = "They're bringing the card machine to your table. Stay comfortable.";
    } else {
      title = 'The cashier sees you';
      body  = "Please head to the counter when you're ready. We're getting your order ready to ring up.";
    }
    tone  = 'accepted';
  } else {
    // PENDING
    if (!isOrder) {
      title = "We're letting your waiter know";
      body  = "They'll be with you in a moment.";
    } else if (isCard) {
      title = "We've got your order";
      body  = 'A waiter is on the way to your table with the card machine.';
    } else {
      title = "We've got your order";
      body  = "When you're ready, please head to the counter to pay. Show this screen or just mention your table number.";
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{ colorScheme: 'light' }}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-up">
        {/* Hero band */}
        <div className="bg-black text-white px-6 pt-7 pb-6 text-center">
          <p className="text-11 font-bold uppercase tracking-[0.25em] text-white/70">{request.tableName}</p>
          {isOrder && (
            <>
              <p className="text-11 font-bold uppercase tracking-widest text-white/60 mt-3">Order code</p>
              <p className="text-44 font-bold leading-none tabular-nums tracking-tight mt-1">#{request.orderCode}</p>
            </>
          )}
          {!isOrder && (
            <div className="mt-3">
              <IconPhoneCall size={36} stroke={2} className="inline-block" />
            </div>
          )}
        </div>

        <div className="px-6 pt-5 pb-3 text-center">
          {tone === 'pending' && (
            <span className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-amber-100 text-amber-800 text-11 font-bold uppercase tracking-wider">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              Waiting for staff
            </span>
          )}
          {tone === 'accepted' && (
            <span className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-emerald-100 text-emerald-800 text-11 font-bold uppercase tracking-wider">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              Confirmed
            </span>
          )}
          {tone === 'paid' && (
            <span className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-emerald-600 text-white text-11 font-bold uppercase tracking-wider">
              <IconCheck size={12} stroke={3} />
              Paid
            </span>
          )}
          {tone === 'cancelled' && (
            <span className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-slate-200 text-slate-700 text-11 font-bold uppercase tracking-wider">Closed</span>
          )}

          <h2 className="text-22 font-bold text-slate-900 leading-tight mt-3">{title}</h2>
          <p className="text-14 text-slate-600 mt-2 leading-snug">{body}</p>

          {submitErr && (
            <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 flex items-start gap-2 text-left">
              <IconAlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-13 font-semibold text-red-700">{submitErr}</p>
            </div>
          )}
        </div>

        <div className="px-6 pb-5" style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }}>
          {tone === 'pending' && (
            <button
              type="button"
              onClick={onCancel}
              className="w-full h-12 rounded-full bg-white border-2 border-slate-200 text-slate-700 text-14 font-bold active:scale-[0.98] transition-transform"
            >
              Cancel
            </button>
          )}
          {(tone === 'paid' || tone === 'cancelled') && (
            <button
              type="button"
              onClick={onDone}
              className="w-full h-12 rounded-full bg-black text-white text-14 font-bold active:scale-[0.98] transition-transform"
            >
              {tone === 'paid' ? 'Done' : 'Start a new order'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
